/**
 * useAficionVisitanteTheater — predicado de elegibilidad y fuente de verdad del
 * texto del modal "Afición visitante".
 *
 * Centraliza la regla que decide si el modal informativo de afición visitante
 * debe mostrarse y qué cuerpo debe renderizar. El PR anterior dejaba el hash
 * literal dentro de la vista de compra, mezclando política de negocio con
 * infraestructura del modal. Esta ubicación única permite que vistas, scripts
 * y tests compartan una sola fuente de verdad y que el magic string nunca se
 * filtre a los consumidores.
 *
 * Diseño:
 *   - `AFICION_VISITANTE_TEATRO_HASH` y `AFICION_VISITANTE_ZONA_TEATRO_HASH`
 *     se exportan como constantes para que los tests y otros módulos puedan
 *     afirmar contra la fuente de verdad sin hardcodear los hashes de nuevo.
 *   - `AFICION_VISITANTE_MODAL_NAME` se exporta para evitar repetir el nombre
 *     del modal cada vez que la vista lo abre o lo cierra.
 *   - `AFICION_VISITANTE_CUERPO_KEYS` es el mapa hash → clave i18n del cuerpo.
 *     Es la fuente de verdad del texto: cada teatro con aviso propio apunta a
 *     su clave de traducción y el default cubre cualquier hash desconocido.
 *   - `isAficionVisitanteTheater(teatroHash)` es un predicado puro: recibe el
 *     hash del teatro y devuelve un booleano. Esto permite usarlo tanto desde
 *     Options API como desde Composition API sin depender de `getCurrentInstance`.
 *   - `getAficionVisitanteCuerpoKey(teatroHash)` devuelve la clave i18n del
 *     cuerpo para el teatro dado, con fallback al texto genérico.
 */

/**
 * Hash del teatro para el cual aplica el aviso de afición visitante.
 * @type {string}
 */
export const AFICION_VISITANTE_TEATRO_HASH = 'PSaG3BjUboDrGdZz6SQq';

/**
 * Hash del teatro con aviso de zona visitante (texto propio).
 * @type {string}
 */
export const AFICION_VISITANTE_ZONA_TEATRO_HASH = 'BHlBbABzmZ5qkeNOHcGH';

/**
 * Nombre del modal en vue-js-modal para abrir/cerrar el aviso.
 * @type {string}
 */
export const AFICION_VISITANTE_MODAL_NAME = 'aficion-visitante';

/**
 * Mapa hash de teatro → clave i18n del cuerpo del aviso. Fuente de verdad del
 * texto: cada teatro con aviso propio apunta a su clave de traducción.
 * @type {Object<string, string>}
 */
const AFICION_VISITANTE_CUERPO_KEYS = {
    [AFICION_VISITANTE_TEATRO_HASH]: 'aficion_visitante.cuerpo',
    [AFICION_VISITANTE_ZONA_TEATRO_HASH]: 'aficion_visitante.cuerpo_zona_visitante'
};

/**
 * Predicado puro que decide si el modal aplica para un teatro dado.
 *
 * @param {string|null|undefined} teatroHash - hash del teatro actual
 * @returns {boolean} true cuando el teatro tiene aviso de afición visitante
 */
export function isAficionVisitanteTheater(teatroHash) {
    return Object.prototype.hasOwnProperty.call(AFICION_VISITANTE_CUERPO_KEYS, teatroHash);
}

/**
 * Devuelve la clave i18n del cuerpo del aviso para un teatro dado.
 *
 * @param {string|null|undefined} teatroHash - hash del teatro actual
 * @returns {string} clave i18n del cuerpo; el texto genérico como fallback
 */
export function getAficionVisitanteCuerpoKey(teatroHash) {
    return AFICION_VISITANTE_CUERPO_KEYS[teatroHash] || 'aficion_visitante.cuerpo';
}
